import { useState, useRef, useEffect, RefObject } from "react";

interface UseTimelineDragProps {
  containerRef: RefObject<HTMLDivElement>;
  timeAxisRef: RefObject<HTMLDivElement>;
  onScrollRatioChange?: (ratio: number) => void;
}

export const useTimelineDrag = ({ containerRef, timeAxisRef, onScrollRatioChange }: UseTimelineDragProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const scrollStartLeft = useRef(0);
  const dragRafRef = useRef<number | null>(null);
  const lastClientXRef = useRef<number>(0);

  /**
   * Start dragging
   */
  const onMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;

    setIsDragging(true);
    dragStartX.current = e.clientX;
    scrollStartLeft.current = containerRef.current.scrollLeft;
    e.preventDefault();
  };

  /**
   * Handle mouse move during drag with RAF optimization
   */
  const onMouseMove = (e: globalThis.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    lastClientXRef.current = e.clientX;

    if (dragRafRef.current !== null) return;

    dragRafRef.current = requestAnimationFrame(() => {
      dragRafRef.current = null;

      const dx = lastClientXRef.current - dragStartX.current;
      const el = containerRef.current!;
      el.scrollLeft = scrollStartLeft.current - dx;

      const maxScroll = el.scrollWidth - el.clientWidth;
      const ratio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
      onScrollRatioChange?.(ratio);

      if (timeAxisRef.current) {
        timeAxisRef.current.scrollLeft = el.scrollLeft;
      }
    });
  };

  /**
   * Stop dragging
   */
  const onMouseUp = () => {
    setIsDragging(false);
  };

  /**
   * Set up global mouse event listeners
   */
  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDragging]);

  return {
    isDragging,
    onMouseDown,
    cursor: isDragging ? "grabbing" : "grab",
    userSelect: isDragging ? "none" : "auto",
  };
};
